import type { ComposerMode } from '../components/ReplyComposer.tsx';

/**
 * Whether a composer attachment becomes the vendor's answer document (as opposed
 * to an admin-only reference example). Mirrors the server rule in
 * apps/api/src/lib/commentAttachment.ts — vendor attachments are always answers;
 * an admin "reference" attachment never becomes the vendor's document. `canPromote`
 * is replyAttachmentCanPromote (@l2u/shared/replyAttachment) for the question; a
 * question that cannot promote never turns an attachment into its answer.
 */
export function attachmentIsAnswer(
  role: 'vendor' | 'admin',
  mode: ComposerMode,
  canPromote: boolean,
): boolean {
  return canPromote && (role === 'vendor' || mode !== 'reference');
}

/**
 * Whether a composer attachment must carry an expiry date before it can be sent.
 * True only for answer documents on a question that offers the expiry field
 * (replyAttachmentOffersExpiry) and is flagged requires_expiry — matching the
 * server's validateAnswerAttachment rejection so the vendor is prompted in the UI
 * instead of being bounced by a 422. A question that offers no expiry field never
 * demands one, even on a stale requires_expiry row: the field is hidden there.
 */
export function attachmentNeedsExpiry(
  role: 'vendor' | 'admin',
  mode: ComposerMode,
  canPromote: boolean,
  requiresExpiry: boolean,
  offersExpiry: boolean,
): boolean {
  return offersExpiry && requiresExpiry && attachmentIsAnswer(role, mode, canPromote);
}
